import React, { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import DateSelector from '../components/life-roi/DateSelector';
import SearchOptions from '../components/life-roi/SearchOptions';
import EventsDisplay from '../components/life-roi/EventsDisplay';
import DataDisplay from '../components/life-roi/DataDisplay';
// TEMPORARILY HIDDEN: import TimeAnalysisChat from '../components/life-roi/TimeAnalysisChat';
import { CalendarEvent } from '../types/models';

interface SearchOptions {
  searchQuery?: string;
  searchType?: 'tag' | 'title';
  useRegex?: boolean;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface TaggedEvents {
  [key: string]: CalendarEvent[];
}

interface HoursByTag {
  [key: string]: number;
}

function LifeROI() {
  const [tags, setTags] = useState({});
  const [keys, setKeys] = useState([]);
  const [dateFilterHours, setDateFilterHours] = useState(0);
  const [dateSelectionString, setDateSelectionString] = useState('');
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({});
  const [currentDateInfo, setCurrentDateInfo] = useState<any>(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [hasEvents, setHasEvents] = useState<boolean>(false);
  const [hasAnyEvents, setHasAnyEvents] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [checkingConnection, setCheckingConnection] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(new Date().getFullYear(), 11, 31)
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  // TEMPORARILY HIDDEN: const [chatExpanded, setChatExpanded] = useState(false);
  const [chartByTag, setChartByTag] = useState(true);
  const [pastOnly, setPastOnly] = useState(true);
  
  useEffect(() => {
    checkGoogleCalendarConnection();
  }, []);

  useEffect(() => {
    if (isConnected) {
      const currentYear = new Date().getFullYear();
      getTags({period: 'year', start: currentYear.toString(), end: '', dateSelectionString: `${currentYear}`});
      collectHistoricalData();
    }
  }, [isConnected]);

  const checkGoogleCalendarConnection = async () => {
    try {
      const response = await axios.get('/api/user/connections');
      setIsConnected(response.data.googleCalendar);

      if (response.data.googleCalendar) {
        const today = new Date().toISOString().split('T')[0];
        const eventsResponse = await axios.get(`/api/calendar/events?period=custom&start=2000-01-01&end=${today}`);
        const eventCount = Object.values(eventsResponse.data).flat().length;
        setHasAnyEvents(eventCount > 0);
        setHasEvents(eventCount > 0);

        await performAutoSync();

        // Re-check events after sync in case this was a first-time visit with no prior events
        if (eventCount === 0) {
          const postSyncResponse = await axios.get(`/api/calendar/events?period=custom&start=2000-01-01&end=${today}`);
          const postSyncCount = Object.values(postSyncResponse.data).flat().length;
          setHasAnyEvents(postSyncCount > 0);
          setHasEvents(postSyncCount > 0);
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error checking connection:', error);
      setIsLoading(false);
    } finally {
      setCheckingConnection(false);
    }
  };

  const performAutoSync = async () => {
    const lastSyncKey = 'lastCalendarSync';
    const lastSync = localStorage.getItem(lastSyncKey);
    const today = new Date().toDateString();

    if (lastSync === today) return;

    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    const startDate = tenYearsAgo.toISOString().split('T')[0];

    setIsSyncing(true);
    try {
      await axios.post('/api/calendar/sync', {
        start_date: startDate,
        end_date: new Date().toISOString().split('T')[0],
        use_include_regex: false,
        use_exclude_regex: false
      });
      localStorage.setItem(lastSyncKey, today);
    } catch (error) {
      console.error('Error during auto-sync:', error);
    } finally {
      setIsSyncing(false);
    }
  };


  const getTags = async (dateInfo, customSearchOptions = null, overridePastOnly?: boolean) => {
    try {
      // Store current date info for search functionality
      setCurrentDateInfo(dateInfo);

      // Use provided search options or fall back to state
      const currentSearchOptions = customSearchOptions || searchOptions;
      const usePastOnly = overridePastOnly !== undefined ? overridePastOnly : pastOnly;

      let url = `/api/calendar/events?period=${dateInfo.period}`;
      if (dateInfo.period === 'custom') {
        url += `&start=${dateInfo.start}&end=${dateInfo.end}`;
      } else {
        url += `&year=${dateInfo.start}`;
        if (dateInfo.period === 'month' || dateInfo.period === 'quarter') {
          url += `&${dateInfo.period}=${dateInfo.end}`;
        }
      }

      if (usePastOnly) {
        url += '&pastOnly=true';
      }

      if (currentSearchOptions.searchQuery) {
        url += `&searchType=${currentSearchOptions.searchType}&searchQuery=${encodeURIComponent(currentSearchOptions.searchQuery)}`;
        if (currentSearchOptions.useRegex) {
          url += '&useRegex=true';
        }
      }

      const response = await axios.get(url);
      const formatData = response.data;
      setTags(formatData);
      setKeys(Object.keys(formatData));
      const eventCount = Object.values(formatData).flat().length;
      setHasEvents(eventCount > 0);
      // Don't update hasAnyEvents here - only update it during initial connection check
      setDateFilterHours(calculateDateFilterHours(dateInfo.start, dateInfo.end, dateInfo.period, usePastOnly));
      setDateSelectionString(buildDateSelectionString(dateInfo, usePastOnly));
      updateChartData(formatData);
      setIsLoading(false);

    } catch (error) {
      console.error('Error fetching events:', error);
      setIsLoading(false);
    }
  };

  const isPeriodInPast = (dateInfo): boolean => {
    const now = new Date();
    let periodEnd: Date;
    if (dateInfo.period === 'year') {
      periodEnd = new Date(parseInt(dateInfo.start), 11, 31, 23, 59, 59);
    } else if (dateInfo.period === 'quarter') {
      const qEnd = parseInt(dateInfo.end) * 3;
      periodEnd = new Date(parseInt(dateInfo.start), qEnd, 0, 23, 59, 59);
    } else if (dateInfo.period === 'month') {
      periodEnd = new Date(parseInt(dateInfo.start), parseInt(dateInfo.end), 0, 23, 59, 59);
    } else {
      periodEnd = new Date(dateInfo.end);
    }
    return periodEnd <= now;
  };

  const buildDateSelectionString = (dateInfo, usePastOnly: boolean): string => {
    const base = dateInfo.dateSelectionString;
    // If pastOnly is off, or the entire period is already in the past, just show the period
    if (!usePastOnly || isPeriodInPast(dateInfo)) {
      return base;
    }
    // Period includes the future — show "As of [current date]"
    const now = new Date();
    const formatted = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    return `${base} — As of ${formatted}`;
  };

  const calculateDateFilterHours = (start, end, period, usePastOnly = false) => {
    const getPeriodHours = () => {
      if (period === 'year') return 365 * 24;
      if (period === 'quarter') return 91 * 24;
      if (period === 'month') return 30 * 24;
      if (start && end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        return Math.round((Number(endDate) - Number(startDate)) / (1000 * 60 * 60));
      }
      return 0;
    };

    if (!usePastOnly) return getPeriodHours();

    // When pastOnly, cap hours to elapsed time from period start to now
    let periodStart: Date;
    if (period === 'year') {
      periodStart = new Date(parseInt(start), 0, 1);
    } else if (period === 'quarter') {
      periodStart = new Date(parseInt(start), (parseInt(end) - 1) * 3, 1);
    } else if (period === 'month') {
      periodStart = new Date(parseInt(start), parseInt(end) - 1, 1);
    } else {
      return getPeriodHours();
    }

    let periodEnd: Date;
    if (period === 'year') {
      periodEnd = new Date(parseInt(start), 11, 31, 23, 59, 59);
    } else if (period === 'quarter') {
      periodEnd = new Date(parseInt(start), parseInt(end) * 3, 0, 23, 59, 59);
    } else if (period === 'month') {
      periodEnd = new Date(parseInt(start), parseInt(end), 0, 23, 59, 59);
    } else {
      periodEnd = new Date();
    }

    const now = new Date();
    // If the period is entirely in the past, return full period hours
    if (periodEnd <= now) return getPeriodHours();
    // Otherwise cap to hours elapsed so far
    const elapsed = Math.round((Number(now) - Number(periodStart)) / (1000 * 60 * 60));
    return Math.max(0, elapsed);
  };

  const updateChartData = (data) => {
    const labels = Object.keys(data);
  
    const chartData = labels.map(label => 
      data[label].reduce((sum, event) => sum + event.duration, 0)
    );
  
    setChartLabels(labels);
    setChartData(chartData);
  };

  const getEventsByTag = () => {
    const tagEvents: TaggedEvents = {};
    console.log(events);
    const filtered = (events as CalendarEvent[]).filter(event => {
      if (!event.tag) return false;
      if (!searchOptions.searchQuery) return true;
      if (searchOptions.searchType === 'tag' && event.tag) {
        return searchOptions.useRegex
          ? new RegExp(searchOptions.searchQuery || '').test(event.tag)
          : event.tag.includes(searchOptions.searchQuery || '');
      }
      return searchOptions.useRegex
        ? new RegExp(searchOptions.searchQuery || '').test(event.title)
        : event.title.includes(searchOptions.searchQuery || '');
    });

    filtered.forEach(event => {
      if (event.tag) {
        if (!tagEvents[event.tag]) {
          tagEvents[event.tag] = [];
        }
        tagEvents[event.tag].push(event);
      }
    });

    return tagEvents;
  };

  const handlePastOnlyToggle = (checked: boolean) => {
    setPastOnly(checked);
    if (currentDateInfo) {
      getTags(currentDateInfo, null, checked);
    }
  };

  const handleSearch = (options) => {
    setSearchOptions(options);
    // Use the stored current date info and pass the new search options directly
    if (currentDateInfo) {
      getTags(currentDateInfo, options);
    } else {
      // Fallback to current year if no date info is available
      const currentYear = new Date().getFullYear();
      getTags({
        period: 'year',
        start: currentYear.toString(),
        end: '',
        dateSelectionString: `${currentYear}`
      }, options);
    }
  };

  const collectHistoricalData = async () => {
    try {
      // Get current date for the end date
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = '2005-01-01'; // Starting from 2005
  
      // Get all events in one request
      const url = `/api/calendar/events?period=custom&start=${startDate}&end=${endDate}`;
      const response = await axios.get(url);
      const allEvents = response.data;
  
      const historicalData = {
        years: {},
        quarters: {},
        months: {}
      };
  
      // Process years
      const years = [...new Set(Object.values(allEvents)
        .flat()
        .map((event: any) => new Date(event.start_time).getFullYear()))].sort();
  
      years.forEach(year => {
        const yearEvents = filterEventsByTimeframe(allEvents, 
          `${year}-01-01`, 
          `${year}-12-31`
        );
        historicalData.years[year] = processTimeframeData(yearEvents, 365 * 24);
      });
  
      // Process quarters
      years.forEach(year => {
        [1, 2, 3, 4].forEach(quarter => {
          const startMonth = (quarter - 1) * 3 + 1;
          const endMonth = quarter * 3;
          const quarterEvents = filterEventsByTimeframe(allEvents,
            `${year}-${String(startMonth).padStart(2, '0')}-01`,
            `${year}-${String(endMonth).padStart(2, '0')}-${endMonth === 3 || endMonth === 12 ? '31' : '30'}`
          );
          historicalData.quarters[`${year}-Q${quarter}`] = processTimeframeData(quarterEvents, 91 * 24);
        });
      });
  
      // Process months
      years.forEach(year => {
        for (let month = 1; month <= 12; month++) {
          const monthStr = String(month).padStart(2, '0');
          const daysInMonth = new Date(year, month, 0).getDate();
          const monthEvents = filterEventsByTimeframe(allEvents,
            `${year}-${monthStr}-01`,
            `${year}-${monthStr}-${daysInMonth}`
          );
          historicalData.months[`${year}-${monthStr}`] = processTimeframeData(monthEvents, daysInMonth * 24);
        }
      });
  
      // Save to database
      await axios.post('/api/calendar/historical-data', { historicalData });
      setHistoricalData(historicalData);
      return historicalData;
    } catch (error) {
      console.error('Error collecting historical data:', error);
    }
  };
  
  const filterEventsByTimeframe = (events, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const filteredEvents = {};
    Object.entries(events).forEach(([tag, tagEvents]) => {
      console.log(tagEvents);
      const filtered = (tagEvents as CalendarEvent[]).filter(event => {
        const eventStart = new Date(event.start_time);
        return eventStart >= start && eventStart <= end;

      });

      if (filtered.length > 0) {
        filteredEvents[tag] = filtered;
      }
    });
    

    return filteredEvents;
  };
  
  const processTimeframeData = (events, totalHours) => {
    const tagTotals = {};
    let allocatedHours = 0;
  
    // Calculate total hours for each tag
    Object.entries(events).forEach(([tag, tagEvents]) => {
      const total = (tagEvents as CalendarEvent[]).reduce((sum, event) => sum + event.duration, 0);
      tagTotals[tag] = total;
      allocatedHours += total;
    });

  
    // Convert to array, sort by total hours, and convert back to object
    const sortedEntries = Object.entries(tagTotals)
      .sort(([, a], [, b]) => Number(b) - Number(a)); // Sort in descending order
  
    // Create new sorted objects
    const sortedTags = {};
    const sortedEvents = {};
  
    sortedEntries.forEach(([tag, total]) => {
      sortedTags[tag] = total;
      sortedEvents[tag] = events[tag];
    });
  
    return {
      tagTotals: sortedTags,
      allocatedHours,
      unallocatedHours: Math.max(0, totalHours - allocatedHours),
      totalHours,
      events: sortedEvents
    };
  };

  if (checkingConnection || isLoading) {
    return (
      <div className="text-center p-4">
        {isSyncing ? (
          <div>
            <p className="text-lg font-medium">Syncing your calendar...</p>
            <p className="text-sm text-gray-500 mt-2">This may take a minute. Your events will appear once the sync is complete.</p>
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  // Show setup guide if not connected or no events ever synced
  if (!isConnected || !hasAnyEvents) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6">Life ROI - Investment Dashboard</h1>
        
        {!isConnected ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">Connect Your Calendar</h2>
            <p className="text-blue-700 mb-6">
              To get started with Life ROI analysis, you need to connect your Google Calendar.
              This allows us to analyze your time allocation and provide insights into your productivity patterns.
            </p>
            <Link 
              to="/life-roi/connections" 
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Set Up Calendar Connection
            </Link>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-semibold text-yellow-800 mb-4">Sync Your Events</h2>
            <p className="text-yellow-700 mb-6">
              Your Google Calendar is connected, but we haven't found any events to analyze yet.
              Sync your calendar events to start seeing insights.
            </p>
            <Link 
              to="/life-roi/connections" 
              className="inline-block bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Sync Calendar Events
            </Link>
          </div>
        )}
        
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold text-gray-800 mb-2">Time Analysis</h3>
            <p className="text-gray-600 text-sm">
              Understand how you spend your time across different activities and projects.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold text-gray-800 mb-2">Productivity Insights</h3>
            <p className="text-gray-600 text-sm">
              Get data-driven insights about your productivity patterns and habits.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold text-gray-800 mb-2">ROI Tracking</h3>
            <p className="text-gray-600 text-sm">
              Measure the return on investment of your time across different activities.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Investment Dashboard</h1>
      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:flex-1">
          <div className="flex items-center gap-4 mb-2">
            <DateSelector onDateChosen={getTags} />
            <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap cursor-pointer">
              <input
                type="checkbox"
                checked={pastOnly}
                onChange={(e) => handlePastOnlyToggle(e.target.checked)}
                className="rounded"
              />
              Past events only
            </label>
          </div>
          <SearchOptions onSearch={handleSearch} />
          <EventsDisplay tags={tags} tagKeys={keys} dateFilterHours={dateFilterHours} dateSelectionString={dateSelectionString} />
          <div className="mt-8">
            <DataDisplay chartLabels={chartLabels} totalHours={dateFilterHours} eventsHourData={chartData} dateSelectionString={dateSelectionString} chartByTag={chartByTag} setChartByTag={setChartByTag} tags={tags} />
          </div>
        </div>
        {/* TEMPORARILY HIDDEN: AI chat drawer
        {historicalData && <TimeAnalysisChat historicalData={historicalData} onExpandedChange={() => {}} />}
        */}
      </div>
  </div>
);
}

export default LifeROI;